import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useInvalidateReturnRequest } from "@babackapp/private-api-client/client/use-invalidate-return-request";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Separator } from "@babackapp/ui/components/separator";
import { UseFormReturn } from "react-hook-form";
import { ReturnRefundForm } from "../../../refund/page";
import { RefundProvider } from "../../../refund/use-refund-context";
import { useCurrentReturnRequest } from "../../../utils/current-return-request-context";
import { useSubmitAcceptReturnRequest } from "./use-submit-accept-return-request";
import useViewerRole from "@/lib/rights/use-viewer-role";
import { useSubmitValidateReturnRequestLineItems } from "./use-submit-validate-return-request-line-items";

export type ConfirmReturnFormValues = {
  restockOnReturn: boolean;
  restockLocationId: string;
  bypassStock: boolean;
  refundAmount: number;
  discountAmount: number;
  returnRequestId: string;
  lineItemsToHandle: unknown;
};

export type ConfirmReturnForm = UseFormReturn<
  ConfirmReturnFormValues,
  unknown,
  undefined
>;

type ConfirmReturnDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  setLoading: (value: boolean) => void;
};

export default function ConfirmReturnDialog({
  open,
  setOpen,
  setLoading
}: ConfirmReturnDialogProps) {
  return (
    <RefundProvider>
      <ConfirmReturnDialogContent open={open} setOpen={setOpen} setLoading={setLoading} />
    </RefundProvider>
  );
}

type ConfirmReturnDialogContentProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  setLoading: (value: boolean) => void;
};

function ConfirmReturnDialogContent({
  open,
  setOpen,
  setLoading,
}: ConfirmReturnDialogContentProps) {
  const t = useTranslation();
  const currentReturnRequest = useCurrentReturnRequest();
  const { data: shop } = useCurrentShopSuspense();
  const invalidateReturnRequest = useInvalidateReturnRequest();
  const role = useViewerRole();

  const submitAcceptReturnRequest = useSubmitAcceptReturnRequest({
    onMutate: () => {
      setOpen(false);
      setLoading(true);
    },
    onSuccess: () => {
      setLoading(false);
      setTimeout(
        () =>
          invalidateReturnRequest({
            returnRequestId: currentReturnRequest.id,
            shopId: shop.id,
          }),
        500,
      );
    },
    onError: () => {
      setLoading(false);
      setTimeout(
        () =>
          invalidateReturnRequest({
            returnRequestId: currentReturnRequest.id,
            shopId: shop.id,
          }),
        500,
      );
    }
  });
  const submitValidateReturnRequestLineItems =
    useSubmitValidateReturnRequestLineItems({
      onMutate: () => setOpen(false),
    });

  const onSubmit =
    role === "logistician"
      ? submitValidateReturnRequestLineItems
      : submitAcceptReturnRequest;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl overflow-y-scroll max-h-[calc(100vh-64px)] flex flex-col pb-0">
        <DialogHeader>
          <DialogTitle>
            {t("returnDetail.confirmModal.titleValidate")}
          </DialogTitle>
        </DialogHeader>
        <Separator className="mt-1 mb-0 pb-0" />
        <ReturnRefundForm
          onSubmit={onSubmit}
          handleClose={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
